import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  Search,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { colors } from '../../theme';

/**
 * Table of Contents component for magazine viewer
 * Displays clickable list of pages with metadata (club name, event name, etc.)
 */
const TableOfContents = ({ pages = [], currentPage, onPageClick, simplified = false }) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [expanded, setExpanded] = useState(true);

  // Filter pages based on search query
  const filteredPages = pages.filter((page) => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    const title = page.title?.toLowerCase() || '';
    const clubName = page.club_name?.toLowerCase() || '';
    const eventName = page.event_name?.toLowerCase() || '';
    const serviceType = page.service_type?.toLowerCase() || '';
    const keywords = (page.keyword_refs || [])
      .map((k) => (typeof k === 'string' ? k : k?.name || ''))
      .join(', ')
      .toLowerCase();
    
    return (
      title.includes(query) ||
      clubName.includes(query) ||
      eventName.includes(query) ||
      serviceType.includes(query) ||
      keywords.includes(query) ||
      page.page_number.toString().includes(query)
    );
  });

  // Group pages by content type (with metadata vs without)
  const pagesWithContent = filteredPages.filter(
    p => p.club_name || p.event_name || p.service_type
  );
  const pagesWithoutContent = filteredPages.filter(
    p => !p.club_name && !p.event_name && !p.service_type
  );

  const toggleExpanded = () => setExpanded(!expanded);

  // Simplified version for dialog - just page number and title
  const renderSimplifiedPageItem = (page) => {
    const isActive = page.page_number === currentPage;
    const title = page.title || page.event_name || page.club_name || t('otherPages');

    return (
      <ListItem
        key={page.id}
        disablePadding
        sx={{
          borderBottom: `1px solid ${colors.lightGray}`,
        }}
      >
        <ListItemButton
          onClick={() => onPageClick(page.page_number)}
          sx={{
            py: 2,
            px: 3,
            display: 'flex',
            justifyContent: 'space-between',
            '&:hover': {
              backgroundColor: 'rgba(0, 51, 141, 0.05)',
            },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: isActive ? 600 : 400,
              color: isActive ? colors.royalBlue : 'text.primary',
              flex: 1,
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: isActive ? 600 : 400,
              color: isActive ? colors.royalBlue : 'text.secondary',
              minWidth: 60,
              textAlign: 'right',
            }}
          >
            {page.page_number}
          </Typography>
        </ListItemButton>
      </ListItem>
    );
  };

  const renderPageItem = (page) => {
    const isActive = page.page_number === currentPage;

    return (
      <ListItem
        key={page.id}
        disablePadding
        sx={{
          borderLeft: isActive ? `4px solid ${colors.lionsGold}` : 'none',
          backgroundColor: isActive ? 'rgba(255, 202, 40, 0.1)' : 'transparent',
        }}
      >
        <ListItemButton
          onClick={() => onPageClick(page.page_number)}
          sx={{
            py: 1,
            px: 2,
            display: 'flex',
            justifyContent: 'center',
            '&:hover': {
              backgroundColor: 'rgba(255, 202, 40, 0.08)',
            },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: isActive ? 600 : 500,
              color: isActive ? colors.royalBlue : 'text.primary',
            }}
          >
            {page.page_number}
          </Typography>
        </ListItemButton>
      </ListItem>
    );
  };

  // Simplified view for dialog - just title and page number like a book
  if (simplified) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Search Box */}
        <Box sx={{ p: 3, borderBottom: `1px solid ${colors.lightGray}` }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('searchPages')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Simplified Page List */}
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <List disablePadding>
            {filteredPages.map(renderSimplifiedPageItem)}
            
            {/* No results */}
            {filteredPages.length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('noResultsFound')}
                </Typography>
              </Box>
            )}
          </List>
        </Box>
      </Box>
    );
  }

  // Full detailed view for sidebar
  return (
    <Paper
      elevation={2}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: `1px solid ${colors.lightGray}`,
          backgroundColor: colors.royalBlue,
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('tableOfContents')}
          </Typography>
          <IconButton size="small" onClick={toggleExpanded} sx={{ color: 'white' }}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
        <Typography variant="caption" sx={{ opacity: 0.9 }}>
          {filteredPages.length} {t('pages')}
        </Typography>
      </Box>

      <Collapse in={expanded} timeout="auto">
        {/* Search Box */}
        <Box sx={{ p: 2, borderBottom: `1px solid ${colors.lightGray}` }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('searchPages')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Page List */}
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          <List disablePadding>
            {/* Pages with content */}
            {pagesWithContent.length > 0 && (
              <>
                {pagesWithContent.map(renderPageItem)}
              </>
            )}

            {/* Separator if both types exist */}
            {pagesWithContent.length > 0 && pagesWithoutContent.length > 0 && (
              <Box sx={{ px: 2, py: 1, backgroundColor: colors.lightGray }}>
                <Typography variant="caption" color="text.secondary">
                  {t('otherPages')}
                </Typography>
              </Box>
            )}

            {/* Pages without content */}
            {pagesWithoutContent.length > 0 && (
              <>
                {pagesWithoutContent.map(renderPageItem)}
              </>
            )}

            {/* No results */}
            {filteredPages.length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('noResultsFound')}
                </Typography>
              </Box>
            )}
          </List>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default TableOfContents;
